import { modal } from '@codeleap/modals'
import { View, Text, Scroll, ActionIcon, Avatar, Emoji, Loading } from '@/components'
import { theme, Chat } from '@/config'
import { UserDetailsModal } from './UserDetailsModal'
import { APIClient } from '@/services'
import { ChatLocalMesage, isOwnReaction, MessageReaction } from '@packages/chat'
import { Channel } from 'stream-chat'
import { Medias } from '@/config/packages'
import { createStyles } from '@codeleap/styles'
import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import { PropsOf } from '@codeleap/types'
import { getFlatlistProps } from '@/utils'

export type ReactionsModalProps = {
  message: ChatLocalMesage
  channel: Channel
}

type ReactionItemProps = Partial<PropsOf<typeof View>> & {
  reaction: MessageReaction
  onPress: (reaction: MessageReaction) => void
  onRemove: (reaction: MessageReaction) => void
  currentUserId: string | undefined
}

function ReactionItem({ reaction, onPress, onRemove, currentUserId, ...props }: ReactionItemProps) {
  const { user, emoji_code } = reaction || {}
  const { src: userImage } = Medias.useResolvedUrl(user?.image as string)
  const ownReaction = isOwnReaction(reaction, currentUserId)
  const onRowClick = ownReaction ? () => onRemove(reaction) : () => onPress(reaction)

  return (
    <View
      debugName={`ReactionItem:${user?.id}`}
      style={styles.reactionWrapper}
      onClick={onRowClick}
      {...props}
    >
      <Avatar
        name={user?.name}
        image={userImage as any}
        style={{ image: styles.avatar, wrapper: styles.avatar }}
        disabled
        firstNameOnly={false}
        debugName={`ReactionAvatar:${user?.id}`}
      />

      <View style={styles.userInfo}>
        <Text
          text={user?.name}
          style={{
            fontSize: 16,
            fontWeight: '500',
            color: theme.colors.textHeadingHighlighted,
          }}
        />
        {ownReaction ? (
          <Text
            text='Tap to remove'
            style={styles.tapToRemove}
          />
        ) : (
          <Text
            text={user?.job_title}
            style={styles.jobTitle}
          />
        )}
      </View>

      <Emoji emoji={emoji_code} size={24} />
    </View>
  )
}

const MemoizedReaction = React.memo(ReactionItem, (prev, next) => {
  const prevId = (prev.reaction as any)?.id
  const nextId = (next.reaction as any)?.id
  const prevUserId = prev.reaction?.user?.id || (prev.reaction as any)?.user_id
  const nextUserId = next.reaction?.user?.id || (next.reaction as any)?.user_id

  return prevId === nextId && prevUserId === nextUserId && prev.onRemove === next.onRemove && prev.currentUserId === next.currentUserId
})

export const ReactionsModal = modal<ReactionsModalProps>({ id: 'reactions-modal' }).content((props) => {
  const { message, channel, toggle } = props

  const currentUser = Chat.Store.useUser()
  const query = APIClient.Chat.useReactions({ messageId: message.id })
  const isInitialLoading = !query?.items?.length && (
    query?.isRefreshing ||
    query?.query?.isLoading ||
    query?.query?.isFetching
  )
  const flatListProps = getFlatlistProps(query, { forceLoading: isInitialLoading })
  const [showScrollbar, setShowScrollbar] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (query?.query?.refetch) {
      query.query.refetch()
    }
  }, [])

  const handlePress = useCallback((reaction: MessageReaction) => {
    toggle()
    setTimeout(() => {
      UserDetailsModal.open({ user: { id: reaction?.user_id, name: reaction?.user?.name, image: reaction?.user?.image } })
    }, 100)
  }, [toggle])

  const handleRemove = useCallback(async (reaction: MessageReaction) => {
    toggle()
    await Chat.Services.removeReaction({ message, channel, reaction })
    query.query.refetch()
  }, [channel, message, toggle, query])

  const deduplicatedItems = useMemo(() => {
    if (!query?.items) return []
    const latestByUserAndType = new Map<string, typeof query.items[0]>()

    query.items.forEach((item) => {
      const userId = item.user?.id || (item as any)?.user_id
      const type = item.type || ''
      const key = `${userId}-${type}`
      const existing = latestByUserAndType.get(key)

      if (!existing) {
        latestByUserAndType.set(key, item)
      } else {
        const existingCreatedAt = (existing as any).created_at || ''
        const itemCreatedAt = (item as any).created_at || ''
        if (itemCreatedAt > existingCreatedAt) {
          latestByUserAndType.set(key, item)
        }
      }
    })

    return Array.from(latestByUserAndType.values())
  }, [query?.items])

  const renderItem = useCallback(({ item }) => {
    return (
      <MemoizedReaction reaction={item} onPress={handlePress} onRemove={handleRemove} currentUserId={currentUser?.id} />
    )
  }, [handlePress, handleRemove, currentUser?.id])

  useEffect(() => {
    if (scrollRef.current) {
      const hasScroll = scrollRef.current.scrollHeight > scrollRef.current.clientHeight
      setShowScrollbar(hasScroll)
    }
  }, [deduplicatedItems])

  ReactionsModal.useProps({
    showClose: false,
    style: ['centered', 'box:responsiveness', {
      box: styles.box,
    }],
  })

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerModal}>
          <View style={{ width: 24, height: 24 }} />

          <Text
            text='Reacts'
            style={['semiBold', 'p2', { color: theme.colors.textHeadingRegular }]}
          />

          <ActionIcon
            icon='x'
            onPress={toggle}
            style={['normalize', 'iconSize:3', 'color:textHeadingRegular']}
          />
        </View>
      </View>

      {isInitialLoading ? (
        <View style={styles.loadingWrapper}>
          <Loading size={theme.size.large} />
        </View>
      ) : (
        <View style={[styles.scrollWrapper]}>
          <View style={{ position: 'relative', height: '100%', width: '100%' }}>
            {showScrollbar && <View style={styles.scrollIndicator} />}
            <View ref={scrollRef} style={{ height: '100%', width: '100%' }}>
              <Scroll
                {...flatListProps}
                data={deduplicatedItems}
                renderItem={renderItem}
                fetchNextPage={query?.getNextPage}
                isFetchingNextPage={query?.isRefreshing}
                isFetching={query?.isRefreshing}
                style={{ wrapper: { height: '100%' } }}
                threshold={10}
              />
            </View>
          </View>
        </View>
      )}
    </View >
  )
})

const styles = createStyles((theme) => ({
  reactionWrapper: {
    ...theme.presets.alignCenter,
    ...theme.presets.row,
    ...theme.presets.justifySpaceBetween,
    ...theme.presets.fullWidth,
    height: 56,
    borderRadius: theme.radius.small,
    gap: 8,
  },
  avatar: {
    width: 40,
    height: 40,
  },
  userInfo: {
    ...theme.presets.column,
    flex: 1,
    gap: theme.spacing.value(0.25),
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headerModal: {
    width: '100%',
    marginBottom: theme.spacing.value(2),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  scrollWrapper: {
    flex: 1,
    overflowY: 'auto',
    width: '100%',
    maxHeight: 330,
    minHeight: 0,
  },
  scrollWrapperLoading: {
    overflowY: 'hidden',
  },
  box: {
    width: '100%',
    maxWidth: 402,
    height: '100%',
    maxHeight: 413,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
  scrollIndicator: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: theme.baseColors.primarySolid500,
    borderRadius: theme.radius.infinite,
    zIndex: 10,
  },
  loadingWrapper: {
    flex: 1,
    ...theme.presets.center,
    ...theme.spacing.paddingVertical(6),
  },
  tapToRemove: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: '15px',
    letterSpacing: 0,
    color: theme.baseColors.primarySolid500,
    cursor: 'pointer',
  },
  jobTitle: {
    fontSize: 12,
    fontWeight: '400',
    color: theme.colors.textCaption,
  },
}))
